// Ad configuration. All flags come from Vite env vars (set only in flagged
// build commands); defaults are ALL-OFF so an unflagged build behaves exactly
// like today. `overrideAdsConfig` exists for tests and staging QA.

export type GateAudience = 'off' | 'anon' | 'anon+free'
export type AdblockPolicy = 'wall' | 'fail-open'

export interface AdsConfig {
  /** Master switch. */
  enabled: boolean
  /** Who gets gated. 'anon' = anonymous only; 'anon+free' also gates free registered users. */
  audience: GateAudience
  /** AdSense publisher id (e.g. "ca-pub-711…"). Empty = no ads ever load. */
  clientId: string
  /** Slot id for the persistent right-rail unit (anonymous users only). Empty = placement hidden. */
  slotRail: string
  /** Slot id for the persistent bottom-strip unit (anonymous users only). Empty = placement hidden. */
  slotBanner: string
  /** Slot id for the persistent layers-panel unit (anonymous users only). Empty = placement hidden. */
  slotLayers: string
  /** Slot id for the mobile-flow bottom banner (anonymous users only). Empty = placement hidden. */
  slotMobile: string
  /** Adds data-adtest="on" to every unit (staging/QA builds — never production). */
  adTest: boolean
  /** Adblock handling: 'wall' refuses the free path; 'fail-open' allows. */
  adblockPolicy: AdblockPolicy
  /** "Continue with ads" countdown before the action proceeds. */
  dwellMs: number
  /** Max wait for the ad SDK to load before deciding blocked/timeout. */
  sdkTimeoutMs: number
  /** Max wait for a unit to report filled/unfilled after the SDK loads. */
  adTimeoutMs: number
}

function envStr(v: unknown, fallback: string): string {
  return typeof v === 'string' && v !== '' ? v : fallback
}
function envBool(v: unknown): boolean {
  return v === '1' || v === 'true' || v === true
}
function envNum(v: unknown, fallback: number): number {
  const n = typeof v === 'string' ? parseInt(v, 10) : NaN
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function fromEnv(): AdsConfig {
  const env = import.meta.env as Record<string, unknown>
  const audience = envStr(env.VITE_ADS_AUDIENCE, 'anon+free')
  const policy = envStr(env.VITE_ADS_ADBLOCK_POLICY, 'wall')
  return {
    enabled: envBool(env.VITE_ADS_ENABLED),
    audience: (['off', 'anon', 'anon+free'] as const).includes(audience as GateAudience)
      ? (audience as GateAudience) : 'anon+free',
    clientId: envStr(env.VITE_ADSENSE_CLIENT, ''),
    slotRail: envStr(env.VITE_ADSENSE_SLOT_RAIL, ''),
    slotBanner: envStr(env.VITE_ADSENSE_SLOT_BANNER, ''),
    slotLayers: envStr(env.VITE_ADSENSE_SLOT_LAYERS, ''),
    slotMobile: envStr(env.VITE_ADSENSE_SLOT_MOBILE, ''),
    adTest: envBool(env.VITE_ADSENSE_TEST),
    adblockPolicy: policy === 'fail-open' ? 'fail-open' : 'wall',
    dwellMs: envNum(env.VITE_ADS_DWELL_MS, 6000),
    sdkTimeoutMs: envNum(env.VITE_ADS_SDK_TIMEOUT_MS, 4000),
    adTimeoutMs: envNum(env.VITE_ADS_AD_TIMEOUT_MS, 12000),
  }
}

let cfg: AdsConfig | null = null

export function adsConfig(): AdsConfig {
  if (!cfg) cfg = fromEnv()
  return cfg
}

/** Runtime override (tests + staging QA). Partial; merges over current config. */
export function overrideAdsConfig(patch: Partial<AdsConfig>): void {
  cfg = { ...adsConfig(), ...patch }
}
